import { useState, type ReactElement } from "react";
import { Checkbox } from "../../../../shared/components/ui/checkbox";
import { TableCell, TableRow } from "../../../../shared/components/ui/table";
import type { BulkEditTitle } from "../types";
import { EditableCell } from "./EditableCell";
import { StatusSelect } from "./StatusSelect";
import { TitleActionsDropdown } from "./TitleActionsDropdown";

interface TitleRowProps {
  readonly title: BulkEditTitle;
  readonly isSelected: boolean;
  readonly onSelect: (titleId: string, selected: boolean) => void;
  readonly onFieldUpdate: (titleId: string, field: string, value: string | number | boolean | null) => Promise<void>;
  readonly onDelete: (titleId: string, titleName: string) => void;
  readonly onDuplicate: (titleId: string) => void;
}

/**
 * Single row in the titles data table with inline editing
 */
export function TitleRow({
  title,
  isSelected,
  onSelect,
  onFieldUpdate,
  onDelete,
  onDuplicate,
}: Readonly<TitleRowProps>): ReactElement {
  const [isTogglingPublish, setIsTogglingPublish] = useState(false);

  const handlePublishToggle = async (published: boolean) => {
    setIsTogglingPublish(true);
    try {
      await onFieldUpdate(title.id, "published", published);
    } finally {
      setIsTogglingPublish(false);
    }
  };

  return (
    <TableRow className="hover:bg-neutral-900/30">
      <TableCell className="w-12">
        <Checkbox checked={isSelected} onCheckedChange={(checked) => onSelect(title.id, checked === true)} />
      </TableCell>
      <TableCell>
        {title.posterUrlHd ? (
          <img src={title.posterUrlHd} alt={title.name} className="w-8 h-12 object-cover rounded" />
        ) : (
          <div className="w-8 h-12 bg-neutral-800 rounded flex items-center justify-center text-xs text-neutral-600">
            —
          </div>
        )}
      </TableCell>
      <TableCell>
        <EditableCell value={title.name} onSave={(value) => onFieldUpdate(title.id, "name", value)} type="text" />
      </TableCell>
      <TableCell>
        <EditableCell value={title.year} onSave={(value) => onFieldUpdate(title.id, "year", value)} type="number" />
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.directorName}
          onSave={(value) => onFieldUpdate(title.id, "directorName", value)}
          type="text"
        />
      </TableCell>
      <TableCell>
        <EditableCell
          value={title.runtimeMinutes}
          onSave={(value) => onFieldUpdate(title.id, "runtimeMinutes", value)}
          type="number"
        />
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {title.genres.slice(0, 2).map((genre) => (
            <span key={genre} className="text-xs px-2 py-0.5 text-neutral-300">
              {genre}
            </span>
          ))}
          {title.genres.length > 2 && <span className="text-xs text-neutral-500">+{title.genres.length - 2}</span>}
        </div>
      </TableCell>
      <TableCell>
        <StatusSelect
          value={title.published}
          onChange={(published) => void handlePublishToggle(published)}
          disabled={isTogglingPublish}
        />
      </TableCell>
      <TableCell>
        <TitleActionsDropdown titleId={title.id} titleName={title.name} onDelete={onDelete} onDuplicate={onDuplicate} />
      </TableCell>
    </TableRow>
  );
}
